/**
 * Pixel geometry for the plan timeline.
 *
 * Structural, not spacing — exactly like `treeGeometry.ts`. The day width is
 * the unit the whole view is built from: bar positions, the grid gradient, the
 * month band and every drag conversion all divide by it, so snapping these to
 * the 8pt grid would put bars and grid lines permanently out of register.
 */

/**
 * One day column when no zoom preference is stored. 7px keeps a quarter
 * (~92 days) on screen at 1440px wide. The e2e suite's drag math assumes this
 * default — `planning-timeline.spec.ts` guards it via `data-day-width`.
 */
export const DEFAULT_DAY_WIDTH_PX = 7;

/** Fully zoomed in: one week fills the track viewport (`timelineZoom.ts`). */
export const ZOOM_MIN_VISIBLE_DAYS = 7;

/** Fully zoomed out: a year fills the track viewport (or the whole window if shorter). */
export const ZOOM_MAX_VISIBLE_DAYS = 365;

/** Multiplicative wheel step: one 100px notch scales the day width by ~×1.22. */
export const WHEEL_ZOOM_SENSITIVITY = 0.002;

/** Below this day width the 1px day gridlines merge into fill; switch to weekly lines. */
export const GRID_DAY_MIN_PX = 3;

/** Fixed row height — no dynamic measurement, unlike the tree's expandable rows. */
export const TIMELINE_ROW_HEIGHT_PX = 34;

/** The frozen name column. */
export const LEFT_COLUMN_WIDTH_PX = 260;

/** Per-level indent in the name column; tighter than the tree's 22px. */
export const TIMELINE_INDENT_PX = 14;

/** Grab zone at each end of a bar for resizing. */
export const RESIZE_HANDLE_PX = 7;

/** Pointer travel before a press counts as a drag rather than a click. */
export const DRAG_THRESHOLD_PX = 3;

/** Trimmed off a bar's width so adjacent bars do not touch. */
export const BAR_INSET_PX = 2;

/** Month header band. */
export const MONTH_BAND_HEIGHT_PX = 28;

export const BAR_HEIGHT_PX = 22;

/**
 * Hit area for the target mark's hover, centred on the tick.
 *
 * Wider than the 1.5px tick because a 1.5px hover target is not one. It is
 * still drawn UNDER the bar (paint order, `TimelineRow`), so widening it cannot
 * eat into a drag — only into empty track.
 */
export const TARGET_HIT_WIDTH_PX = 16;

/** Duration given to a task scheduled by clicking an empty track. */
export const DEFAULT_BLOCK_DAYS = 5;

/** Slack each side of the plan's extent, so a bar can be dragged past it. */
export const WINDOW_PAD_DAYS = 30;

/** Window around today for a plan where nothing is scheduled yet. */
export const EMPTY_WINDOW_BEFORE_DAYS = 7;
export const EMPTY_WINDOW_AFTER_DAYS = 60;
